/** Types for the Range Planning "Range Overview" demo screen - the control tower view across
 * customers, contracts, site groups and sites. Demo Mode only - no real backend exists for the
 * concept of a "Range" yet; it sits one level above a Menu, adding the customer/client account/
 * site group commercial context and estate-wide readiness/publishing state that a Menu record
 * alone doesn't carry. Where a range is built from a real Menu, `menuId` links back to it so
 * "Compare planned vs live" can read that menu's real version history. */

export enum RangeStatus {
  Draft = 'Draft',
  InReview = 'InReview',
  Approved = 'Approved',
  Scheduled = 'Scheduled',
  Live = 'Live',
  Expired = 'Expired',
  Archived = 'Archived',
  Blocked = 'Blocked',
}

/** Readiness of a single dimension (cost / allergen / carbon) for a range going live. */
export enum ReadinessStatus {
  Ready = 'Ready',
  Attention = 'Attention',
  Blocked = 'Blocked',
}

export type RangeScopeType = 'Site' | 'MultiSite' | 'ClientAccount' | 'SiteGroup' | 'WholeEstate';

export interface RangeSite {
  siteName: string;
  region: string;
}

export interface RangeTargets {
  averageSellingPrice: number;
  averageFoodCostPercentage: number;
  grossMarginPercentage: number;
  plantBasedDishPercentage: number;
  britishSourcedIngredientPercentage: number;
  localSourcingWithinMiles: number;
  localSourcingPercentage: number;
  maximumWastePercentage: number;
  customerSatisfactionScore: number;
  repeatPurchasePercentage: number;
}

export interface Range {
  id: number;
  name: string;
  /** Links back to a real Menu record (src/mocks/data/menusData.ts) when this range is built
   * from one - null for demo-only ranges that don't yet have a menu authored. */
  menuId: number | null;
  customerName: string;
  clientAccount: string;
  siteGroup: string;
  sites: RangeSite[];
  servicePeriod: string;
  activeFrom: string;
  activeTo: string | null;
  status: RangeStatus;
  costStatus: ReadinessStatus;
  costStatusNote: string;
  allergenStatus: ReadinessStatus;
  allergenStatusNote: string;
  carbonStatus: ReadinessStatus;
  carbonStatusNote: string;
  lastPublished: string | null;
  /** Premium range fields (source dataset's `ranges[]` record) - additive, kept alongside the
   * control-tower fields above rather than replacing them. See rangesData.ts header comment
   * for the RangeStatus mapping used to derive `status` from the source `status` string. */
  code?: string;
  type?: string;
  owner?: string;
  serviceModel?: string[];
  availableFrom?: string;
  availableUntil?: string;
  season?: string;
  targetAudience?: string;
  pricePosition?: string;
  description?: string;
  /** Chef-led philosophy bullet points - drives the premium narrative section on the Range
   * Development page so the range reads as chef-authored rather than generic corporate copy. */
  principles?: string[];
  targets?: RangeTargets;
}

export interface RangeListResponse {
  data: Range[];
  totalCount: number;
}

export interface RangeTile {
  key: string;
  labelKey: string;
  defaultLabel: string;
  value: number;
  href: string;
}
